import { ActionIcon, Indicator, Popover, Stack, Group, Text, Badge, CloseButton, Button, Divider } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { useState, useEffect, useRef } from 'react';
import { useNotifications, type Notification } from '../hooks/useNotifications';
import { getDaysUntil } from '../utils/holidays';

const AUTO_OPENED_KEY = 'ts-notifications-auto-opened';

function NotificationItem({
  notification,
  onDismiss,
}: {
  notification: Notification;
  onDismiss: () => void;
}) {
  const daysUntil = getDaysUntil(notification.date);
  const isUrgent = daysUntil <= 1;

  return (
    <Group
      gap="sm"
      p="xs"
      style={{
        backgroundColor: isUrgent ? 'var(--mantine-color-orange-0)' : undefined,
        borderRadius: 'var(--mantine-radius-sm)',
      }}
    >
      <Stack gap={2} style={{ flex: 1 }}>
        <Group gap="xs">
          <Text size="sm" fw={600}>
            {notification.title}
          </Text>
          {notification.market && (
            <Badge size="xs" variant="light" color={notification.market === 'canada' ? 'red' : 'blue'}>
              {notification.market === 'canada' ? 'CA' : 'US'}
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          {notification.message}
        </Text>
      </Stack>
      <CloseButton size="sm" onClick={onDismiss} />
    </Group>
  );
}

export function NotificationCenter() {
  const [opened, setOpened] = useState(false);
  const hasAutoOpened = useRef(false);
  const {
    notifications,
    dismissNotification,
    dismissAll,
    hasNotifications,
    notificationCount,
  } = useNotifications();

  // Auto-open on login if there are notifications (once per session)
  useEffect(() => {
    if (hasNotifications && !hasAutoOpened.current) {
      // Check if we've already auto-opened today
      const lastAutoOpened = sessionStorage.getItem(AUTO_OPENED_KEY);
      const today = new Date().toDateString();

      if (lastAutoOpened !== today) {
        // Small delay to let the UI settle after login
        const timer = setTimeout(() => {
          setOpened(true);
          sessionStorage.setItem(AUTO_OPENED_KEY, today);
        }, 500);
        hasAutoOpened.current = true;
        return () => clearTimeout(timer);
      }
      hasAutoOpened.current = true;
    }
  }, [hasNotifications]);

  return (
    <Popover
      width={320}
      position="bottom-end"
      shadow="md"
      opened={opened}
      onChange={setOpened}
    >
      <Popover.Target>
        <Indicator
          color="orange"
          size={8}
          disabled={!hasNotifications}
          processing={hasNotifications}
        >
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => setOpened((o) => !o)}
            aria-label="Notifications"
          >
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600} size="sm">
              Notifications
            </Text>
            {notificationCount > 0 && (
              <Badge size="sm" variant="light">
                {notificationCount}
              </Badge>
            )}
          </Group>

          <Divider />

          {notifications.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No upcoming holidays
            </Text>
          ) : (
            <>
              <Stack gap="xs">
                {notifications.map((n) => (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    onDismiss={() => dismissNotification(n.id)}
                  />
                ))}
              </Stack>

              {notifications.length > 1 && (
                <>
                  <Divider />
                  <Button
                    variant="subtle"
                    size="xs"
                    fullWidth
                    onClick={() => {
                      dismissAll();
                      setOpened(false);
                    }}
                  >
                    Dismiss All
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
